import { useEffect, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dispatchApi, directoryApi, type DispatchQueueItem, type DriverDirectoryEntry } from "@/lib/api";
import { formatNaira } from "@/lib/mock-data";
import { useAuth } from "@/stores/auth";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { PackageSearch, Send } from "lucide-react";

export default function Dispatch() {
  const token = useAuth((s) => s.session?.accessToken);
  const [queue, setQueue] = useState<DispatchQueueItem[]>([]);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assigning, setAssigning] = useState<string | null>(null);
  const [bulkDriver, setBulkDriver] = useState<string>("");

  const load = () => {
    setLoading(true);
    Promise.all([dispatchApi.queue(undefined, token), directoryApi.drivers(token)])
      .then(([q, d]) => {
        setQueue(q.items);
        setDrivers(d);
      })
      .catch(() => toast.error("Failed to load dispatch queue"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const assignOne = async (parcelId: string) => {
    const driverId = picked[parcelId];
    if (!driverId) return toast.error("Pick a driver first");
    setAssigning(parcelId);
    try {
      await dispatchApi.assign(parcelId, { driver_id: driverId }, token);
      toast.success("Assigned");
      setQueue((q) => q.filter((p) => p.id !== parcelId));
      setSelected((s) => {
        const next = new Set(s);
        next.delete(parcelId);
        return next;
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not assign");
    } finally {
      setAssigning(null);
    }
  };

  const assignSelected = async () => {
    if (!bulkDriver || selected.size === 0) return;
    const assignments = Array.from(selected).map((parcel_id) => ({ parcel_id, driver_id: bulkDriver }));
    try {
      const res = await dispatchApi.bulkAssign(assignments, token);
      if (res.succeeded.length) {
        toast.success(`Assigned ${res.succeeded.length} parcel(s)`);
        setQueue((q) => q.filter((p) => !res.succeeded.includes(p.id)));
      }
      if (res.failed.length) {
        toast.error(`${res.failed.length} failed — ${res.failed[0].error}`);
      }
      setSelected(new Set());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk assign failed");
    }
  };

  const toggle = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <Topbar
        title="Dispatch"
        subtitle="Unassigned parcels awaiting a driver"
        actions={
          selected.size > 0 ? (
            <div className="flex items-center gap-2">
              <Select value={bulkDriver} onValueChange={setBulkDriver}>
                <SelectTrigger className="w-48"><SelectValue placeholder="Assign selected to…" /></SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" disabled={!bulkDriver} onClick={assignSelected}>
                <Send className="mr-1.5 h-4 w-4" /> Assign {selected.size}
              </Button>
            </div>
          ) : null
        }
      />
      <div className="flex-1 space-y-6 p-4 md:p-6">
        <section className="surface-card">
          {loading ? (
            <LoadingBlock label="Loading queue…" />
          ) : queue.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground">
              <PackageSearch className="h-8 w-8" />
              Queue is empty — every intake parcel has a driver.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead>Pickup</TableHead>
                  <TableHead>Dropoff</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Waiting</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.tracking_id}</TableCell>
                    <TableCell className="max-w-56 truncate text-sm">{p.pickup_addr}</TableCell>
                    <TableCell className="max-w-56 truncate text-sm">{p.dropoff_addr}</TableCell>
                    <TableCell className="text-sm">{formatNaira(p.amount_kobo / 100)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{Math.round(p.waiting_minutes)}m</TableCell>
                    <TableCell>
                      <Select value={picked[p.id] ?? ""} onValueChange={(v) => setPicked((cur) => ({ ...cur, [p.id]: v }))}>
                        <SelectTrigger className="w-40"><SelectValue placeholder="Select driver" /></SelectTrigger>
                        <SelectContent>
                          {drivers.map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" disabled={!picked[p.id] || assigning === p.id} onClick={() => assignOne(p.id)}>
                        Assign
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </div>
    </>
  );
}
